import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MARKDOWN_PREVIEW_UNSUPPORTED_MESSAGE, type ResourceRootView } from '../appService';
import type { ResourceFileListItem, ResourceFolderListItem, SongListItem } from '../../resources/view';
import { useAppRuntime, useWorkspaceBootstrapContext, useWorkspaceContext, useWorkspaceRouteAdapter } from './context';
import {
  notificationListQueryKey,
  resourcesRootQueryKey,
  songDetailQueryKey,
  songListQueryKey,
  unreadNotificationStateQueryKey,
  useResourcesRootQuery,
  useSongDetailQuery,
  useSongListQuery
} from './data';
import {
  ActionError,
  BackHeader,
  CompactEmptyRow,
  PendingButton,
  RecoverableReadError,
  SectionLoading,
  SvgIcon,
  TextField,
  WorkspaceModuleTopBar,
  formatActionError
} from './ui';

export function WorkspaceResourcesTabContent(): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const resourcesRoot = useResourcesRootQuery();
  const songs = useSongListQuery();

  return (
    <>
      <WorkspaceModuleTopBar title="资料" />
      <div className="resource-workbench">
        <section className="resource-section" aria-labelledby="ordinary-resources-heading">
          <div className="resource-toolbar">
            <h2 id="ordinary-resources-heading">普通资料</h2>
          </div>
          <ResourcesRootContent resourcesRoot={resourcesRoot} />
        </section>
        <ScoresSection
          resourcesRoot={resourcesRoot.data}
          songs={songs}
          canManageSongs={appContext.isCurrentUserAdmin}
        />
      </div>
    </>
  );
}

function ResourcesRootContent({
  resourcesRoot
}: {
  readonly resourcesRoot: ReturnType<typeof useResourcesRootQuery>;
}): ReactNode {
  if (resourcesRoot.status === 'pending') {
    return <SectionLoading body="正在读取普通资料。" />;
  }

  if (resourcesRoot.status === 'error') {
    return (
      <RecoverableReadError
        error={resourcesRoot.error}
        retryAction="retry-resources-root"
        onRetry={() => void resourcesRoot.refetch()}
      />
    );
  }

  return (
    <ResourceRows folders={resourcesRoot.data.folders} files={resourcesRoot.data.files} emptyBody="暂无普通资料" />
  );
}

function ScoresSection({
  resourcesRoot,
  songs,
  canManageSongs
}: {
  readonly resourcesRoot: ResourceRootView | undefined;
  readonly songs: ReturnType<typeof useSongListQuery>;
  readonly canManageSongs: boolean;
}): ReactNode {
  const songCount = resourcesRoot?.scoresEntry.songCount ?? songs.data?.length ?? 0;
  const scoreLabel = resourcesRoot?.scoresEntry.label ?? '乐谱';

  return (
    <section className="resource-section" aria-labelledby="scores-heading">
      <div className="resource-toolbar">
        <div className="resource-score-entry" aria-labelledby="scores-heading">
          <SvgIcon name="music" className="resource-row-icon" />
          <span>
            <strong id="scores-heading">{scoreLabel}</strong>
            <small>{songCount} 首曲目</small>
          </span>
        </div>
        {canManageSongs ? <CreateSongInlineForm /> : null}
      </div>
      <SongListContent songs={songs} />
    </section>
  );
}

function SongListContent({ songs }: { readonly songs: ReturnType<typeof useSongListQuery> }): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();

  if (songs.status === 'pending') {
    return <SectionLoading body="正在读取曲目。" />;
  }

  if (songs.status === 'error') {
    return (
      <RecoverableReadError error={songs.error} retryAction="retry-song-list" onRetry={() => void songs.refetch()} />
    );
  }

  if (songs.data.length === 0) {
    return <CompactEmptyRow body="暂无曲目" />;
  }

  return (
    <section className="prototype-list" role="list" aria-label="曲目">
      {songs.data.map((song) => (
        <div className="resource-list-item" role="listitem" key={song.songId}>
          <SongRow song={song} onOpen={() => routeAdapter.openSongDetail(song.songId)} />
        </div>
      ))}
    </section>
  );
}

function SongRow({ song, onOpen }: { readonly song: SongListItem; readonly onOpen: () => void }): ReactNode {
  return (
    <button
      type="button"
      className="resource-row prototype-list-row"
      data-action="open-song-detail"
      data-song-id={song.songId}
      onClick={onOpen}
    >
      <SvgIcon name="music" className="resource-row-icon" />
      <span className="member-copy">
        <strong>{song.name}</strong>
        <small>{song.currentScoreFileName ?? '暂无当前乐谱'}</small>
      </span>
      <span className="row-chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

export function CreateSongInlineForm(): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const routeAdapter = useWorkspaceRouteAdapter();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef(false);

  async function submit(): Promise<void> {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    setPending(true);
    setError(null);
    try {
      const song = await service.createSong({ workspaceContext, name });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: resourcesRootQueryKey(workspaceContext), exact: true }),
        queryClient.invalidateQueries({ queryKey: songListQueryKey(workspaceContext), exact: true })
      ]);
      routeAdapter.openSongDetail(song.songId);
    } catch (caught) {
      setError(formatError(caught));
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  }

  if (!isOpen) {
    return (
      <button type="button" className="secondary-button" data-action="create-song" onClick={() => setIsOpen(true)}>
        新建曲目
      </button>
    );
  }

  return (
    <form
      className="create-song-inline-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <TextField label="曲目名称" field="song-name" name="song-name" value={name} onChange={setName} />
      <PendingButton
        action="create-song"
        label="创建"
        variant="primary"
        pending={pending}
        onClick={() => void submit()}
      />
      <ActionError message={error} />
    </form>
  );
}

export function SongDetailPage({ songId }: { readonly songId: string }): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const detail = useSongDetailQuery(songId);

  return (
    <div className="route-outlet" data-screen="song-detail">
      <BackHeader
        title={detail.data?.song.name ?? '曲目详情'}
        action="back-to-resources"
        label="返回资料"
        onBack={routeAdapter.backToResources}
      />
      <SongDetailContent detail={detail} songId={songId} />
    </div>
  );
}

function SongDetailContent({
  detail,
  songId
}: {
  readonly detail: ReturnType<typeof useSongDetailQuery>;
  readonly songId: string;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const appContext = useWorkspaceBootstrapContext();
  const [currentScoreUpdateNote, setCurrentScoreUpdateNote] = useState<string | null>(null);

  if (detail.status === 'pending') {
    return <SectionLoading body="正在读取曲目详情。" />;
  }

  if (detail.status === 'error') {
    return (
      <RecoverableReadError
        error={detail.error}
        retryAction="retry-song-detail"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  return (
    <>
      <CurrentScorePanel
        songId={songId}
        currentScore={detail.data.currentScore}
        canManage={appContext.isCurrentUserAdmin}
        updateNote={currentScoreUpdateNote}
        onUpdated={(updateNote) => setCurrentScoreUpdateNote(updateNote)}
      />
      <RelatedResourcesPanel
        songId={songId}
        currentFolderId={detail.data.relatedResources.folderId}
        folders={detail.data.relatedResources.folders}
        files={detail.data.relatedResources.files}
        canManage={appContext.isCurrentUserAdmin}
        onOpenFolder={(folderId) => routeAdapter.openSongRelatedFolder(songId, folderId)}
      />
    </>
  );
}

function CurrentScorePanel({
  songId,
  currentScore,
  canManage,
  updateNote,
  onUpdated
}: {
  readonly songId: string;
  readonly currentScore: ResourceFileListItem | null;
  readonly canManage: boolean;
  readonly updateNote: string | null;
  readonly onUpdated: (updateNote: string | null) => void;
}): ReactNode {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="current-score-panel" aria-labelledby="current-score-heading">
      <div className="resource-toolbar">
        <h2 id="current-score-heading">当前乐谱</h2>
        {canManage && !isOpen ? (
          <button
            type="button"
            className="secondary-button"
            data-action="open-current-score-update"
            onClick={() => setIsOpen(true)}
          >
            更新当前乐谱
          </button>
        ) : null}
      </div>
      <div data-current-score="pinned">
        {currentScore ? (
          <ResourceFileRow file={currentScore} kindOverride="current-score" />
        ) : (
          <CompactEmptyRow body="暂无当前乐谱" />
        )}
      </div>
      {updateNote ? <p>{updateNote}</p> : null}
      {isOpen ? (
        <CurrentScoreUpdateForm
          songId={songId}
          onCancel={() => setIsOpen(false)}
          onUpdated={(note) => {
            onUpdated(note);
            setIsOpen(false);
          }}
        />
      ) : null}
    </section>
  );
}

export function CurrentScoreUpdateForm({
  songId,
  onCancel,
  onUpdated
}: {
  readonly songId: string;
  readonly onCancel: () => void;
  readonly onUpdated: (updateNote: string | null) => void;
}): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [updateNote, setUpdateNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const submitGuard = useSingleFlightGuard();
  const updateCurrentScore = useMutation({
    mutationFn: async () => {
      const file = fileInputRef.current?.files?.[0] ?? null;
      if (!file) {
        throw new Error('Resource file upload failed');
      }
      const intent = await uploadResourceFileForService(service, workspaceContext, file);
      return await service.updateSongCurrentScore({
        workspaceContext,
        songId,
        fileUploadId: intent.fileUploadId,
        updateNote
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: songDetailQueryKey(workspaceContext, songId, null), exact: true }),
        queryClient.invalidateQueries({ queryKey: songListQueryKey(workspaceContext), exact: true }),
        queryClient.invalidateQueries({ queryKey: notificationListQueryKey(workspaceContext), exact: true }),
        queryClient.invalidateQueries({ queryKey: unreadNotificationStateQueryKey(workspaceContext), exact: true })
      ]);
      const trimmedNote = updateNote.trim();
      onUpdated(trimmedNote === '' ? null : trimmedNote);
    }
  });

  return (
    <form
      className="form-stack"
      onSubmit={(event) => {
        event.preventDefault();
        void submitMutation(updateCurrentScore.mutateAsync, setActionError, submitGuard);
      }}
    >
      <label className="field">
        <span>乐谱文件</span>
        <input ref={fileInputRef} type="file" required />
      </label>
      <label className="field">
        <span>更新说明</span>
        <textarea value={updateNote} onChange={(event) => setUpdateNote(event.currentTarget.value)} />
      </label>
      <ActionError message={actionError} />
      <div className="footer-actions">
        <button type="button" className="secondary-button" onClick={onCancel}>
          取消
        </button>
        <PendingButton
          action="update-current-score"
          label="确认更新"
          variant="primary"
          pending={updateCurrentScore.isPending}
          onClick={() => void submitMutation(updateCurrentScore.mutateAsync, setActionError, submitGuard)}
        />
      </div>
    </form>
  );
}

export function SongRelatedFolderPage({
  songId,
  folderId
}: {
  readonly songId: string;
  readonly folderId: string;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const detail = useSongDetailQuery(songId, folderId);

  return (
    <div className="route-outlet" data-screen="song-related-folder">
      <BackHeader
        title="相关资源"
        action="back-to-song-detail"
        label="返回曲目详情"
        onBack={() => routeAdapter.backToSongDetail(songId)}
      />
      <SongRelatedFolderContent detail={detail} songId={songId} />
    </div>
  );
}

function SongRelatedFolderContent({
  detail,
  songId
}: {
  readonly detail: ReturnType<typeof useSongDetailQuery>;
  readonly songId: string;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const appContext = useWorkspaceBootstrapContext();

  if (detail.status === 'pending') {
    return <SectionLoading body="正在读取相关资源。" />;
  }

  if (detail.status === 'error') {
    return (
      <RecoverableReadError
        error={detail.error}
        retryAction="retry-song-related-folder"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  return (
    <RelatedResourcesPanel
      headingId="related-folder-heading"
      heading={detail.data.song.name}
      songId={songId}
      currentFolderId={detail.data.relatedResources.folderId}
      folders={detail.data.relatedResources.folders}
      files={detail.data.relatedResources.files}
      canManage={appContext.isCurrentUserAdmin}
      onOpenFolder={(childFolderId) => routeAdapter.openSongRelatedFolder(songId, childFolderId)}
    />
  );
}

function RelatedResourcesPanel({
  headingId = 'related-resources-heading',
  heading = '相关资源',
  songId,
  currentFolderId,
  folders,
  files,
  canManage,
  onOpenFolder
}: {
  readonly headingId?: string;
  readonly heading?: string;
  readonly songId: string;
  readonly currentFolderId: string | null;
  readonly folders: readonly ResourceFolderListItem[];
  readonly files: readonly ResourceFileListItem[];
  readonly canManage: boolean;
  readonly onOpenFolder: (folderId: string) => void;
}): ReactNode {
  const [activeForm, setActiveForm] = useState<'upload' | 'folder' | null>(null);

  return (
    <section className="resource-section" aria-labelledby={headingId}>
      <div className="resource-toolbar">
        <h2 id={headingId}>{heading}</h2>
        {canManage && !activeForm ? <RelatedResourceAddMenu onSelect={(form) => setActiveForm(form)} /> : null}
      </div>
      {activeForm === 'upload' ? (
        <RelatedResourceUploadForm
          songId={songId}
          currentFolderId={currentFolderId}
          onCancel={() => setActiveForm(null)}
          onUploaded={() => setActiveForm(null)}
        />
      ) : null}
      {activeForm === 'folder' ? (
        <RelatedFolderCreateForm
          songId={songId}
          currentFolderId={currentFolderId}
          onCancel={() => setActiveForm(null)}
          onCreated={() => setActiveForm(null)}
        />
      ) : null}
      <ResourceRows folders={folders} files={files} emptyBody="暂无相关资源" onOpenFolder={onOpenFolder} />
    </section>
  );
}

function RelatedResourceAddMenu({ onSelect }: { readonly onSelect: (form: 'upload' | 'folder') => void }): ReactNode {
  const [isOpen, setIsOpen] = useState(false);
  const menuId = 'related-resource-add-menu';

  return (
    <>
      <button
        type="button"
        className="icon-secondary-button"
        data-action="open-related-resource-add-menu"
        aria-label={isOpen ? '收起新增相关资源选项' : '新增相关资源'}
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setIsOpen((current) => !current)}
      >
        <SvgIcon name="plus" className="icon-secondary-button-svg" />
      </button>
      <div
        className="related-resource-add-menu"
        id={menuId}
        role="group"
        data-related-resource-add-menu={isOpen ? 'true' : 'false'}
        aria-label="新增相关资源选项"
        hidden={!isOpen}
      >
        <button
          type="button"
          className="resource-menu-action prototype-list-row"
          data-action="choose-upload-related-resource"
          aria-label="上传文件"
          onClick={() => onSelect('upload')}
        >
          <SvgIcon name="file" className="resource-row-icon" />
          <span className="member-copy">
            <strong>上传文件</strong>
            <small>添加 Markdown、MP3 或其他文件</small>
          </span>
        </button>
        <button
          type="button"
          className="resource-menu-action prototype-list-row"
          data-action="choose-create-related-folder"
          aria-label="创建文件夹"
          onClick={() => onSelect('folder')}
        >
          <SvgIcon name="folder" className="resource-row-icon" />
          <span className="member-copy">
            <strong>创建文件夹</strong>
            <small>整理当前曲目的相关资源</small>
          </span>
        </button>
      </div>
    </>
  );
}

export function RelatedResourceUploadForm({
  songId,
  currentFolderId,
  onCancel,
  onUploaded
}: {
  readonly songId: string;
  readonly currentFolderId: string | null;
  readonly onCancel: () => void;
  readonly onUploaded: () => void;
}): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const submitGuard = useSingleFlightGuard();
  const uploadRelatedResource = useMutation({
    mutationFn: async () => {
      const file = fileInputRef.current?.files?.[0] ?? null;
      if (!file) {
        throw new Error('Resource file upload failed');
      }
      const intent = await uploadResourceFileForService(service, workspaceContext, file);
      return await service.attachSongRelatedResourceFile({
        workspaceContext,
        songId,
        parentFolderId: currentFolderId,
        fileUploadId: intent.fileUploadId
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: songDetailQueryKey(workspaceContext, songId, currentFolderId),
        exact: true
      });
      onUploaded();
    }
  });

  return (
    <form
      className="form-stack"
      onSubmit={(event) => {
        event.preventDefault();
        void submitMutation(uploadRelatedResource.mutateAsync, setActionError, submitGuard);
      }}
    >
      <label className="field">
        <span>资源文件</span>
        <input ref={fileInputRef} type="file" required />
      </label>
      <ActionError message={actionError} />
      <div className="footer-actions">
        <button type="button" className="secondary-button" onClick={onCancel}>
          取消
        </button>
        <PendingButton
          action="upload-related-resource"
          label="上传"
          variant="primary"
          pending={uploadRelatedResource.isPending}
          onClick={() => void submitMutation(uploadRelatedResource.mutateAsync, setActionError, submitGuard)}
        />
      </div>
    </form>
  );
}

export function RelatedFolderCreateForm({
  songId,
  currentFolderId,
  onCancel,
  onCreated
}: {
  readonly songId: string;
  readonly currentFolderId: string | null;
  readonly onCancel: () => void;
  readonly onCreated: () => void;
}): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const submitGuard = useSingleFlightGuard();
  const createFolder = useMutation({
    mutationFn: () =>
      service.createSongRelatedFolder({
        workspaceContext,
        songId,
        parentFolderId: currentFolderId,
        name
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: songDetailQueryKey(workspaceContext, songId, currentFolderId),
        exact: true
      });
      onCreated();
    }
  });

  return (
    <form
      className="create-song-inline-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submitMutation(createFolder.mutateAsync, setActionError, submitGuard);
      }}
    >
      <TextField
        label="文件夹名称"
        field="related-folder-name"
        name="related-folder-name"
        value={name}
        onChange={setName}
      />
      <div className="footer-actions">
        <button type="button" className="secondary-button" onClick={onCancel}>
          取消
        </button>
        <PendingButton
          action="create-related-folder"
          label="创建"
          variant="primary"
          pending={createFolder.isPending}
          onClick={() => void submitMutation(createFolder.mutateAsync, setActionError, submitGuard)}
        />
      </div>
      <ActionError message={actionError} />
    </form>
  );
}

export function ResourceRows({
  folders,
  files,
  emptyBody,
  onOpenFolder
}: {
  readonly folders: readonly ResourceFolderListItem[];
  readonly files: readonly ResourceFileListItem[];
  readonly emptyBody: string;
  readonly onOpenFolder?: (folderId: string) => void;
}): ReactNode {
  if (folders.length === 0 && files.length === 0) {
    return <CompactEmptyRow body={emptyBody} />;
  }

  return (
    <section className="prototype-list" role="list">
      {folders.map((folder) => (
        <div className="resource-list-item" role="listitem" key={folder.resourceFolderId}>
          <ResourceFolderRow
            folder={folder}
            onOpen={onOpenFolder ? () => onOpenFolder(folder.resourceFolderId) : undefined}
            action={onOpenFolder ? 'open-resource-folder' : undefined}
          />
        </div>
      ))}
      {files.map((file) => (
        <div className="resource-list-item" role="listitem" key={file.resourceFileId}>
          <ResourceFileRow file={file} />
        </div>
      ))}
    </section>
  );
}

function ResourceFolderRow({
  folder,
  onOpen,
  action
}: {
  readonly folder: ResourceFolderListItem;
  readonly onOpen?: () => void;
  readonly action?: string;
}): ReactNode {
  const body = (
    <>
      <span className="resource-kind-icon" data-resource-kind="folder">
        <SvgIcon name="folder" className="resource-row-icon" />
      </span>
      <span className="member-copy">
        <strong>{folder.name}</strong>
        <small>文件夹</small>
      </span>
      {onOpen ? (
        <span className="row-chevron" aria-hidden="true">
          ›
        </span>
      ) : null}
    </>
  );

  if (onOpen) {
    return (
      <button
        type="button"
        className="resource-row prototype-list-row"
        data-action={action}
        data-resource-folder-id={folder.resourceFolderId}
        onClick={onOpen}
      >
        {body}
      </button>
    );
  }

  return <div className="resource-row prototype-list-row">{body}</div>;
}

type ResourceKind = 'folder' | 'current-score' | 'markdown' | 'audio' | 'file';

function ResourceFileRow({
  file,
  kindOverride
}: {
  readonly file: ResourceFileListItem;
  readonly kindOverride?: ResourceKind;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const kind = kindOverride ?? getResourceFileKind(file);
  const meta = getResourceFileMeta(file, kind);
  const body = (
    <>
      <span className="resource-kind-icon" data-resource-kind={kind}>
        <SvgIcon name={getResourceKindIcon(kind)} className="resource-row-icon" />
      </span>
      <span className="member-copy">
        <strong>{file.fileName}</strong>
        <small>
          {meta} · {formatSize(file.sizeBytes)}
        </small>
      </span>
      <span className="row-chevron" aria-hidden="true">
        ›
      </span>
    </>
  );

  return (
    <button
      type="button"
      className="resource-row prototype-list-row"
      data-resource-kind={kind}
      data-action="open-resource-file"
      data-resource-file-id={file.resourceFileId}
      onClick={() => routeAdapter.openResourceFile(file.resourceFileId)}
    >
      {body}
    </button>
  );
}

function getResourceFileKind(file: ResourceFileListItem): ResourceKind {
  if (file.extension.toLowerCase() === 'md') {
    return 'markdown';
  }
  if (file.extension.toLowerCase() === 'mp3') {
    return 'audio';
  }
  return 'file';
}

function getResourceFileMeta(file: ResourceFileListItem, kind: ResourceKind): string {
  if (kind === 'current-score') {
    return `当前乐谱 · ${file.extension.toUpperCase()}`;
  }
  if (kind === 'markdown') {
    return 'Markdown 文档';
  }
  if (kind === 'audio') {
    return 'MP3 音频';
  }
  return `${file.extension.toUpperCase()} 文件`;
}

function getResourceKindIcon(kind: ResourceKind): Parameters<typeof SvgIcon>[0]['name'] {
  if (kind === 'folder') {
    return 'folder';
  }
  if (kind === 'current-score') {
    return 'fileScore';
  }
  if (kind === 'markdown') {
    return 'fileText';
  }
  if (kind === 'audio') {
    return 'fileAudio';
  }
  return 'file';
}

type ResourceFileAccess = Awaited<ReturnType<ReturnType<typeof useAppRuntime>['service']['getResourceFileAccess']>>;

type ResourcePreviewState =
  | { readonly status: 'pending' }
  | { readonly status: 'error'; readonly error: Error }
  | { readonly status: 'markdown'; readonly fileName: string; readonly markdown: string }
  | { readonly status: 'access'; readonly access: ResourceFileAccess };

export function ResourceFilePreviewPage({ resourceFileId }: { readonly resourceFileId: string }): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const { routerHistory, service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const [preview, setPreview] = useState<ResourcePreviewState>({ status: 'pending' });
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let isActive = true;
    async function loadPreview(): Promise<void> {
      setPreview({ status: 'pending' });
      try {
        try {
          const markdown = await service.readMarkdownResourceFile({ workspaceContext, resourceFileId });
          if (isActive) {
            setPreview({ status: 'markdown', fileName: markdown.fileName, markdown: markdown.markdown });
          }
          return;
        } catch (caught) {
          if (!isMarkdownPreviewUnsupportedError(caught)) {
            throw caught;
          }
        }

        const access = await service.getResourceFileAccess({ workspaceContext, resourceFileId });
        if (isActive) {
          setPreview({ status: 'access', access });
        }
      } catch (caught) {
        if (isActive) {
          setPreview({
            status: 'error',
            error: caught instanceof Error ? caught : new Error('Resource preview failed')
          });
        }
      }
    }

    void loadPreview();

    return () => {
      isActive = false;
    };
  }, [resourceFileId, retryCount, service, workspaceContext]);

  const backToResourceContext = (): void => {
    if (routerHistory.canGoBack()) {
      routerHistory.back();
      return;
    }
    routeAdapter.backToResources();
  };

  return (
    <div className="route-outlet" data-screen="resource-preview">
      <BackHeader
        title={getResourcePreviewTitle(preview)}
        action="back-to-resource-context"
        label="返回上一页"
        onBack={backToResourceContext}
      />
      <ResourcePreviewContent preview={preview} onRetry={() => setRetryCount((current) => current + 1)} />
    </div>
  );
}

function ResourcePreviewContent({
  preview,
  onRetry
}: {
  readonly preview: ResourcePreviewState;
  readonly onRetry: () => void;
}): ReactNode {
  if (preview.status === 'pending') {
    return <SectionLoading body="正在读取资料。" />;
  }

  if (preview.status === 'error') {
    return <RecoverableReadError error={preview.error} retryAction="retry-resource-preview" onRetry={onRetry} />;
  }

  if (preview.status === 'markdown') {
    return (
      <section className="resource-preview-content" data-resource-preview-context="markdown">
        <ResourcePreviewFileContext fileName={preview.fileName} kind="markdown" />
        <div className="resource-preview-document">{renderMarkdown(preview.markdown)}</div>
      </section>
    );
  }

  if (isAudioResourceAccess(preview.access)) {
    return (
      <section className="resource-preview-content" data-resource-preview-context="audio">
        <ResourcePreviewFileContext fileName={preview.access.fileName} kind="audio" />
        <audio controls className="resource-audio-player" data-resource-audio-player="true" src={preview.access.url} />
      </section>
    );
  }

  return (
    <section className="resource-preview-content" data-resource-preview-context="file">
      <ResourcePreviewFileContext fileName={preview.access.fileName} kind="file" extension={preview.access.extension} />
      <a className="resource-download-link" href={preview.access.url} download={preview.access.fileName}>
        下载文件
      </a>
    </section>
  );
}

function ResourcePreviewFileContext({
  fileName,
  kind,
  extension
}: {
  readonly fileName: string;
  readonly kind: ResourceKind;
  readonly extension?: string;
}): ReactNode {
  const label =
    kind === 'markdown'
      ? 'Markdown 文档'
      : kind === 'audio'
        ? 'MP3 音频'
        : `${(extension ?? 'file').toUpperCase()} 文件`;

  return (
    <header className="resource-preview-file-context">
      <span className="resource-kind-icon" data-resource-kind={kind}>
        <SvgIcon name={getResourceKindIcon(kind)} className="resource-row-icon" />
      </span>
      <span className="member-copy">
        <strong>{fileName}</strong>
        <small>{label}</small>
      </span>
    </header>
  );
}

function getResourcePreviewTitle(preview: ResourcePreviewState): string {
  if (preview.status === 'markdown') {
    return preview.fileName;
  }
  if (preview.status === 'access') {
    return preview.access.fileName;
  }
  return '资料预览';
}

function isMarkdownPreviewUnsupportedError(caught: unknown): boolean {
  return caught instanceof Error && caught.message === MARKDOWN_PREVIEW_UNSUPPORTED_MESSAGE;
}

function isAudioResourceAccess(access: ResourceFileAccess): boolean {
  return access.mimeType === 'audio/mpeg' || access.extension === 'mp3';
}

function renderMarkdown(markdown: string): ReactNode {
  const nodes: ReactNode[] = [];
  let paragraphLines: string[] = [];
  let listItems: string[] = [];
  let key = 0;

  function flushParagraph(): void {
    if (paragraphLines.length === 0) {
      return;
    }
    nodes.push(<p key={`p-${key++}`}>{paragraphLines.join(' ')}</p>);
    paragraphLines = [];
  }

  function flushList(): void {
    if (listItems.length === 0) {
      return;
    }
    nodes.push(
      <ul key={`ul-${key++}`}>
        {listItems.map((item, index) => (
          <li key={`${key}-${index}`}>{item}</li>
        ))}
      </ul>
    );
    listItems = [];
  }

  for (const line of markdown.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed === '') {
      flushParagraph();
      flushList();
      continue;
    }
    if (trimmed.startsWith('# ')) {
      flushParagraph();
      flushList();
      nodes.push(<h2 key={`h2-${key++}`}>{trimmed.slice(2).trim()}</h2>);
      continue;
    }
    if (trimmed.startsWith('## ')) {
      flushParagraph();
      flushList();
      nodes.push(<h3 key={`h3-${key++}`}>{trimmed.slice(3).trim()}</h3>);
      continue;
    }
    if (trimmed.startsWith('- ')) {
      flushParagraph();
      listItems.push(trimmed.slice(2).trim());
      continue;
    }
    flushList();
    paragraphLines.push(trimmed);
  }

  flushParagraph();
  flushList();

  return nodes.length === 0 ? <p>暂无内容</p> : nodes;
}

function formatSize(sizeBytes: number): string {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${Math.round(sizeBytes / 1024)} KB`;
  }
  return `${(sizeBytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatError(error: unknown): string {
  return formatActionError(error);
}

async function uploadResourceFileForService(
  service: ReturnType<typeof useAppRuntime>['service'],
  workspaceContext: ReturnType<typeof useWorkspaceContext>,
  file: File
): Promise<{
  readonly fileUploadId: string;
  readonly objectKey: string;
}> {
  const intent = await service.createResourceUploadIntent({
    workspaceContext,
    fileName: file.name,
    mimeType: file.type,
    sizeBytes: file.size
  });
  await service.uploadResourceFile({ upload: intent.upload, file });
  await service.completeUpload({
    fileUploadId: intent.fileUploadId,
    objectKey: intent.objectKey,
    sizeBytes: file.size,
    etag: null
  });
  return intent;
}

async function submitMutation(
  mutateAsync: () => Promise<unknown>,
  setActionError: (message: string | null) => void,
  guard: ReturnType<typeof useSingleFlightGuard>
): Promise<void> {
  if (!guard.begin()) {
    return;
  }
  setActionError(null);
  try {
    await mutateAsync();
  } catch (error) {
    setActionError(formatActionError(error));
  } finally {
    guard.end();
  }
}

function useSingleFlightGuard(): {
  readonly begin: () => boolean;
  readonly end: () => void;
} {
  const pendingRef = useRef(false);
  return {
    begin: () => {
      if (pendingRef.current) {
        return false;
      }
      pendingRef.current = true;
      return true;
    },
    end: () => {
      pendingRef.current = false;
    }
  };
}
